// Mobile search and filters component
// CREATED: Combines search bar with filter button for mobile-optimized interface
// Features:
// - Full-width search bar
// - Compact filter button with active filter badge
// - Results counter display
// - Touch-target optimized (44px min height)

import { SearchBar } from './SearchBar';
import { Filter } from 'lucide-react';

interface MobileSearchFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onFilterClick?: () => void;
  activeFilterCount?: number;
  resultCount?: number;
  showFilterButton?: boolean;
  searchPlaceholder?: string;
}

export function MobileSearchFilters({
  searchTerm,
  onSearchChange,
  onFilterClick,
  activeFilterCount = 0,
  resultCount,
  showFilterButton = false,
  searchPlaceholder = 'Rechercher...',
}: MobileSearchFiltersProps) {
  return (
    <div className="space-y-3">
      {/* Search and filter row */}
      <div className="flex items-center gap-2">
        {/* Search bar - takes most of the space */}
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        </div>

        {/* Filter button - compact */}
        {showFilterButton && onFilterClick && (
          <button
            onClick={onFilterClick}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 border rounded-lg transition-all font-medium flex-shrink-0 ${
              activeFilterCount > 0
                ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
            style={{ minHeight: '44px' }}
            aria-label="Filtres"
          >
            <Filter size={18} />
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white text-xs font-semibold">
                {activeFilterCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Results counter */}
      {resultCount !== undefined && (
        <div className="flex items-center justify-between px-1">
          <span className="text-sm text-gray-600">
            {resultCount === 0 ? (
              'Aucun résultat'
            ) : (
              <>
                <span className="font-semibold text-gray-900">{resultCount}</span> résultat
                {resultCount > 1 ? 's' : ''}
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
